import React from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Columns, Element } from 'react-bulma-components'
import OptimizedImage from '../optimized-image/optimized-image'
import { Translate } from '../../utils/translations'
import { campaignApi } from '../../campaigns/api'
import type { QuantityStepProps } from './quantity-step-props'

type Sd500QuantityFormValues = { sd500Qty?: number; emd500eQty?: number }

/** Allowed quantity range per scooter model on the quantity step. */
const MIN_SCOOTER_QUANTITY = 0
const MAX_SCOOTER_QUANTITY = 9
const QUANTITY_RANGE_MESSAGE = '0-9'

/**
 * Returns true when the value is within the allowed 0-9 range.
 * Undefined is treated as valid here (empty field handled by the "at least one" rule).
 */
const isQuantityInRange = (value: number | undefined): boolean => {
  if (value === undefined) return true
  const quantity = Number(value)
  return !isNaN(quantity) && quantity >= MIN_SCOOTER_QUANTITY && quantity <= MAX_SCOOTER_QUANTITY
}

/**
 * Quantity step for the SD500 / EMD500E campaign.
 *
 * Collects the owned quantity for both products. Unlike the B900 crank step,
 * the rule here is "at least one product" (sum of both quantities must be >= 1).
 */
const Sd500QuantityForm: React.FC<QuantityStepProps> = ({
  onSuccess,
  requestor,
  language,
  submissionId,
  savedValues,
  onSaveValues,
}) => {
  const useCreateQuantity = campaignApi.useCreateQuantity
  const productQuantityMutation = useCreateQuantity({
    mutation: {
      onSuccess: data => {
        onSuccess(data.data)
      },
      onError: error => {
        console.error('error', error)
      },
    },
  })

  /**
   * Submits the collected quantities to the SD500/EMD500E product-quantity endpoint.
   */
  const handleSubmit = async (values: Sd500QuantityFormValues) => {
    if (!submissionId) {
      return
    }
    onSaveValues?.(values)
    await productQuantityMutation.mutateAsync({
      data: {
        submissionId,
        requestor: {
          country: requestor.country,
          email: requestor.email,
          language,
        },
        quantity: {
          sd500: Number(values.sd500Qty) || 0,
          emd500e: Number(values.emd500eQty) || 0,
        },
      },
    })
  }

  return (
    <Formik
      enableReinitialize
      initialValues={
        {
          sd500Qty: savedValues?.sd500Qty,
          emd500eQty: savedValues?.emd500eQty,
        } as Sd500QuantityFormValues
      }
      validate={values => {
        const errors: Partial<Record<keyof Sd500QuantityFormValues, string>> = {}
        const sd500 = Number(values.sd500Qty)
        const emd500e = Number(values.emd500eQty)

        if (!isQuantityInRange(values.sd500Qty)) {
          errors.sd500Qty = QUANTITY_RANGE_MESSAGE
        }
        if (!isQuantityInRange(values.emd500eQty)) {
          errors.emd500eQty = QUANTITY_RANGE_MESSAGE
        }

        // At least one product is required (sum must be >= 1)
        if (!errors.sd500Qty && !errors.emd500eQty && (sd500 || 0) + (emd500e || 0) < 1) {
          errors.sd500Qty = QUANTITY_RANGE_MESSAGE
          errors.emd500eQty = QUANTITY_RANGE_MESSAGE
        }

        return errors
      }}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          {/* Help block: shows where to read the model number on the scooter so
              the user can tell SD500E and MD500E apart before entering quantities. */}
          <Element mb={4} textAlign="center">
            <Element mb={2}>
              <Translate value="recall.modelNumberHelp" />
            </Element>
            <div style={{ maxWidth: 720, margin: 'auto' }}>
              <OptimizedImage
                imageName="sd500-em500e-model-number_2"
                alt="Emplacement de la référence modèle SD500E / MD500E"
              />
            </div>
          </Element>
          {/* Visual separator between the help image and the quantity selection. */}
          <hr />
          <Columns>
            <Columns.Column size={6} style={{ textAlign: 'center' }}>
              <OptimizedImage imageName="sd500" alt="Produit SD500" priority />
              <Element mt={2} mb={2} textWeight="bold">
                <Translate value="recall.descriptionSd500" />
              </Element>
              <div style={{ maxWidth: 200, margin: 'auto' }}>
                <label htmlFor="sd500Qty" style={{ fontWeight: 600 }}>
                  <Translate value="recall.quantity" />
                </label>
                <Field
                  id="sd500Qty"
                  name="sd500Qty"
                  type="number"
                  min={MIN_SCOOTER_QUANTITY}
                  max={MAX_SCOOTER_QUANTITY}
                  className="input"
                  placeholder="..."
                />
                {errors.sd500Qty && touched.sd500Qty && (
                  <Element textColor="danger" mt={1} style={{ fontSize: 14 }}>
                    {errors.sd500Qty}
                  </Element>
                )}
              </div>
            </Columns.Column>
            <Columns.Column size={6} style={{ textAlign: 'center' }}>
              <OptimizedImage imageName="emd500e" alt="Produit EMD500E" priority />
              <Element mt={2} mb={2} textWeight="bold">
                <Translate value="recall.descriptionEmd500e" />
              </Element>
              <div style={{ maxWidth: 200, margin: 'auto' }}>
                <label htmlFor="emd500eQty" style={{ fontWeight: 600 }}>
                  <Translate value="recall.quantity" />
                </label>
                <Field
                  id="emd500eQty"
                  name="emd500eQty"
                  type="number"
                  min={MIN_SCOOTER_QUANTITY}
                  max={MAX_SCOOTER_QUANTITY}
                  className="input"
                  placeholder="..."
                />
                {errors.emd500eQty && touched.emd500eQty && (
                  <Element textColor="danger" mt={1} style={{ fontSize: 14 }}>
                    {errors.emd500eQty}
                  </Element>
                )}
              </div>
            </Columns.Column>
          </Columns>
          <Element mt={4} textAlign="center">
            <Button color="primary" type="submit" disabled={isSubmitting} data-testid="step-submit">
              <Translate value="form.submit" />
            </Button>
          </Element>
        </Form>
      )}
    </Formik>
  )
}

export default Sd500QuantityForm
